import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { statsAPI, affairesAPI, echeancesAPI } from '../lib/api';
import { formatDate, truncate } from '../lib/utils';
import { 
  FolderOpen, 
  Users, 
  CalendarDays, 
  AlertCircle,
  ArrowRight,
  MessageSquare,
  Clock,
  Scale,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, onClick }) => (
  <div 
    className="diplomat-card p-4 sm:p-5 cursor-pointer group active:scale-[0.98] transition-transform" 
    onClick={onClick}
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-[#94A3B8] mb-2 sm:mb-3">{label}</p>
        <p className="text-3xl sm:text-4xl font-heading font-semibold text-[#F8FAFC]">{value}</p>
      </div>
      <div className="p-2 sm:p-2.5 border border-[#B69D74]/20 group-hover:border-[#B69D74]/40 transition-all duration-300">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#B69D74]" strokeWidth={1.5} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentAffaires, setRecentAffaires] = useState([]);
  const [upcomingEcheances, setUpcomingEcheances] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, affairesRes, echeancesRes] = await Promise.all([
        statsAPI.get(),
        affairesAPI.getAll('en_cours'),
        echeancesAPI.getAll()
      ]);
      setStats(statsRes.data);
      
      // S'assurer que les données sont des tableaux
      const affairesData = Array.isArray(affairesRes.data) ? affairesRes.data : [];
      const echeancesData = Array.isArray(echeancesRes.data) ? echeancesRes.data : [];
      
      setRecentAffaires(affairesData.slice(0, 5));
      setUpcomingEcheances(
        echeancesData
          .filter(e => !e.completed)
          .sort((a, b) => new Date(a.date_echeance) - new Date(b.date_echeance))
          .slice(0, 5)
      );
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Tableau de bord">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Tableau de bord">
      <div className="space-y-4 sm:space-y-6 animate-fade-in">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard 
            icon={FolderOpen} 
            label="DOSSIERS EN COURS" 
            value={stats?.affaires_en_cours || 0}
            onClick={() => navigate('/affaires')}
          />
          <StatCard 
            icon={Users} 
            label="CLIENTS" 
            value={stats?.clients || 0}
            onClick={() => navigate('/clients')}
          />
          <StatCard 
            icon={AlertCircle} 
            label="ÉCHÉANCES" 
            value={stats?.echeances_pending || 0}
            onClick={() => navigate('/calendrier')}
          />
          <StatCard 
            icon={CalendarDays} 
            label="AUDIENCES" 
            value={stats?.audiences || 0}
            onClick={() => navigate('/calendrier')}
          />
        </div>

        {/* Quick Actions */}
        <div className="diplomat-card p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <Zap className="w-5 h-5 text-[#B69D74] flex-shrink-0" strokeWidth={1.5} />
              <div>
                <h2 className="text-sm font-medium text-[#F8FAFC]">Assistant IA</h2>
                <p className="text-xs text-[#94A3B8]">
                  Posez vos questions juridiques à l'IA
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/chat')}
              className="w-full sm:w-auto bg-[#B69D74] text-[#0F172A] font-semibold uppercase tracking-wider text-xs hover:bg-[#C5AE87] gap-2 h-12 sm:h-10 px-6 transition-all duration-300"
              data-testid="start-chat-btn"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
              Nouvelle conversation
            </Button>
          </div>
        </div>

        {/* Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {/* Recent Affaires */}
          <div className="diplomat-card">
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-[#B69D74]/10">
              <h3 className="text-xs font-heading font-semibold uppercase tracking-wider text-[#F5F5EF]">Dossiers récents</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/affaires')}
                className="text-[#94A3B8] hover:text-[#B69D74] hover:bg-transparent gap-1 h-8 sm:h-7 text-xs"
                data-testid="view-all-affaires"
              >
                Voir tout
                <ArrowRight className="w-3 h-3" strokeWidth={2} />
              </Button>
            </div>
            <div className="p-2">
              {recentAffaires.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-8 text-center">
                  Aucun dossier en cours
                </p>
              ) : (
                <div className="space-y-1">
                  {recentAffaires.map((affaire) => (
                    <div 
                      key={affaire.id}
                      className="flex items-center justify-between p-3 hover:bg-white/5 active:bg-white/10 cursor-pointer transition-colors duration-200 rounded-lg"
                      onClick={() => navigate(`/affaires/${affaire.id}`)}
                      data-testid={`affaire-${affaire.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Scale className="w-4 h-4 text-[#B69D74] flex-shrink-0" strokeWidth={1.5} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-[#F5F5EF] truncate">{affaire.intitule}</p>
                          <p className="text-xs text-[#94A3B8] font-mono">{affaire.reference}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] sm:text-xs px-2 py-1 domain-${affaire.domaine_juridique} flex-shrink-0 ml-2`}>
                        {affaire.domaine_juridique}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Echeances */}
          <div className="diplomat-card">
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-[#B69D74]/10">
              <h3 className="text-xs font-heading font-semibold uppercase tracking-wider text-[#F5F5EF]">Prochaines échéances</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/calendrier')}
                className="text-[#94A3B8] hover:text-[#B69D74] hover:bg-transparent gap-1 h-8 sm:h-7 text-xs"
                data-testid="view-all-echeances"
              >
                Voir tout
                <ArrowRight className="w-3 h-3" strokeWidth={2} />
              </Button>
            </div>
            <div className="p-2">
              {upcomingEcheances.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-8 text-center">
                  Aucune échéance à venir
                </p>
              ) : (
                <div className="space-y-1">
                  {upcomingEcheances.map((echeance) => (
                    <div 
                      key={echeance.id}
                      className="flex items-center justify-between p-3 hover:bg-white/5 active:bg-white/10 cursor-pointer transition-colors duration-200 rounded-lg"
                      onClick={() => navigate('/calendrier')}
                      data-testid={`echeance-${echeance.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" strokeWidth={1.5} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-[#F5F5EF] truncate">{echeance.titre}</p>
                          <p className="text-xs text-[#94A3B8]">{echeance.type}</p>
                        </div>
                      </div>
                      <span className="text-[10px] sm:text-xs text-[#94A3B8] font-mono flex-shrink-0 ml-2">
                        {formatDate(echeance.date_echeance)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
